

import { Islands, Vertex3 } from '@rta/rta-blueprint-library';

import { ComponentController } from '../../core/controller/component-controller.class';
import { DisplayMode } from '@r-core';
import { IslandConnectionActions } from '../../components/action-menu/island-connection';
import { RenderIslandOverhead } from '../render/render-island-overhead';
import { CreateCornerPattern, CreateCountertopPattern } from '../symbols';

export const showOverheadIslands = (islands: Islands, controller: ComponentController, islandIdFilter: Array<string> | null = null): React.ReactElement => {
	const { options, navigation } = controller;
	const isSkuLayerActive = navigation.displayMode === DisplayMode.SkuOverhead || navigation.displayMode === DisplayMode.SkuOverheadManufacturing;
	// console.log('getGroupedIslands:kitchenSize', controller.kitchen.size);

	// Scope to a subset (e.g. a joint countertop sheet shows only its own in-line islands).
	const scopedIslands = islandIdFilter ? islands.children.filter((island) => islandIdFilter.includes(island.id)) : islands.children;
	const sortedIslands = scopedIslands.sort((a, b) => a.islandSize.height - b.islandSize.height);
	const connectionLinks = [];

	const sortedIslandElements = sortedIslands
		.map((island) => {
			const islandId = island.id;
			const { origin, axis } = island;

			const sourceConnections = island.connections.getSourceConnections();
			const [firstConnection] = sourceConnections;
			const targetInScope = firstConnection?.targetIsland && (!islandIdFilter || islandIdFilter.includes(firstConnection.targetIsland.id));
			if (sourceConnections.length > 0 && targetInScope) {
				const [{ sourcePanel, targetIsland }] = sourceConnections;
				const targetOrigin1 = targetIsland.origin;
				const sourcePoint = sourceConnections[0].getSourcePoint()
					.rotateAxis(new Vertex3(), axis.y, 'y')
					.add(origin);
				const targetPoint = sourceConnections[0].getTargetPoint()
					.rotateAxis(new Vertex3(), targetIsland.axis.y, 'y')
					.add(targetOrigin1);
				const actionPoint = { x: (sourcePoint.x + targetPoint.x) / 2, y: (sourcePoint.z + targetPoint.z) / 2 };

				connectionLinks.push(<g key={`${islandId}-connection`}>
					{/* <circle cx={sourcePoint.x} cy={sourcePoint.z} r=".25" fill="black" /> */}
					<line x1={sourcePoint.x} y1={sourcePoint.z} x2={targetPoint.x} y2={targetPoint.z} stroke="black" strokeWidth=".125" strokeDasharray={0.5} />

					{!options.readonly && <IslandConnectionActions panelId={sourcePanel.id} offset={{ x: actionPoint.x, y: actionPoint.y }} />}
				</g>);
			}

			const node = <g key={`island-${islandId}`} fill="blue"

			transform={`
				translate(${island.center.x}, ${island.center.z})
				rotate(${island.axis.y})
			`}>
				<CreateCountertopPattern countertop={island.getCountertop()} options={options} />
				<CreateCornerPattern material={island.refMap?.kitchen?.material ?? null} options={options} />
				<RenderIslandOverhead islandId={ island.id } renderDimensions={isSkuLayerActive ? 'skip' : 'all'}/>

				{ /* developer mode: island */ }
				{ options.showDeveloperInsights && <>
					{/* light green - rotation point for group */}
					<circle cx={'0'} cy={'0'} r="1.0" fill="red" opacity={0.75}/>

					{ /* green - center point of the group */ }
					<circle cx={`${island.islandSize.width / -2}`} cy={`${island.islandSize.depth / 2}`} r="0.5" fill="green" opacity={0.75}/>


					{ /* dark green - outside corner points */ }
					{ island.panels.children.map((p) => {
						const cornerPoints = p.getCornerPoints(false, { nearestSixteenth: true, translate: true });
						if (!cornerPoints) return null;
						const { corners } = cornerPoints;
						const [p1, p2] = corners;
						return <g key={p.id}>
							<circle cx={p1.x} cy={p1.z} r="0.5" fill="darkgreen" opacity={0.8}/>
							<circle cx={p2.x} cy={p2.z} r="0.5" fill="darkgreen" opacity={0.8}/>
						</g>
					})
					}

					{ /* light green - inside corner points */ }
					{ island.panels.children.map((p) => {
						const cornerPoints = p.getCornerPoints(null, { nearestSixteenth: true, translate: true });
						if (!cornerPoints) return null;
						const { corners } = cornerPoints;
						const [p1, p2] = corners;
						return <g key={p.id}>
							<circle cx={p1.x} cy={p1.z} r="0.3" fill="lightgreen" opacity={0.8}/>
							<circle cx={p2.x} cy={p2.z} r="0.3" fill="lightgreen" opacity={0.8}/>
						</g>
					})
					}

					{ /* red - trim origin */ }
					{ island.panelTrims.children.map((panelTrim) => {
						const ptOrigin = panelTrim.origin;
						return <circle key={panelTrim.id} cx={ptOrigin.x} cy={ptOrigin.z} r=".25" fill="red" />
					})}

					{/* green - island origin */}
					<circle cx={0} cy={0} r="0.8" fill="green" opacity={0.75}/>
				</>}
			</g>;

			return node;
		});

	return <g key={`${controller.kitchen.id}-islands`}>
		{sortedIslandElements}
		{connectionLinks}
	</g>;
};
